import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { fn, hash, concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { and, eq } from 'ember-truth-helpers';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import set from 'ember-set-helper/helpers/set';
import {
  HdsLinkStandalone,
  HdsFormTextInputField,
  HdsTabs,
  HdsCodeBlock,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import { Typography, FlexGrid, DisclosureGuide } from 'core/components';
import { boxPadding } from 'core/modifiers';
import templateString from 'core/utils/template-string';
import Accordion from 'networks-common/components/networks/accordion';
import List from 'hcp/components/page/hvn-routes/list';

let consulIngress = templateString`aws ec2 --region ${'region'} authorize-security-group-ingress --group-id ${'groupId'} --ip-permissions
 IpProtocol=tcp,FromPort=8301,ToPort=8301,IpRanges='[{CidrIp=${'cidrBlock'}}]'
 IpProtocol=udp,FromPort=8301,ToPort=8301,IpRanges='[{CidrIp=${'cidrBlock'}}]'
 IpProtocol=tcp,FromPort=8301,ToPort=8301,UserIdGroupPairs='[{GroupId=${'groupId'}}]'
 IpProtocol=udp,FromPort=8301,ToPort=8301,UserIdGroupPairs='[{GroupId=${'groupId'}}]'`;

let consulEgressAgentless = templateString`aws ec2 --region ${'region'} authorize-security-group-egress --group-id ${'groupId'} --ip-permissions
 IpProtocol=tcp,FromPort=8300,ToPort=8300,IpRanges='[{CidrIp=${'cidrBlock'}}]'
 IpProtocol=tcp,FromPort=8301,ToPort=8301,IpRanges='[{CidrIp=${'cidrBlock'}}]'
 IpProtocol=udp,FromPort=8301,ToPort=8301,IpRanges='[{CidrIp=${'cidrBlock'}}]'
 IpProtocol=tcp,FromPort=8301,ToPort=8301,UserIdGroupPairs='[{GroupId=${'groupId'}}]'
 IpProtocol=udp,FromPort=8301,ToPort=8301,UserIdGroupPairs='[{GroupId=${'groupId'}}]'
 IpProtocol=tcp,FromPort=80,ToPort=80,IpRanges='[{CidrIp=${'cidrBlock'}}]'
 IpProtocol=tcp,FromPort=443,ToPort=443,IpRanges='[{CidrIp=${'cidrBlock'}}]'
 IpProtocol=tcp,FromPort=8502,ToPort=8502,IpRanges='[{CidrIp=${'cidrBlock'}}]'`;

let vaultTemplate = templateString`aws ec2 --region ${'region'} authorize-security-group-egress --group-id ${'groupId_flag'} --ip-permissions IpProtocol=tcp,FromPort=8200,ToPort=8200,IpRanges='[{CidrIp=${'cidrIp_tcp_port8200'}}]'`;

/**
 *
 * `PageHvnPeeringsConfigurationAccordionAwsCli` description here.
 *
 *
 * ```
 * <Page::HvnPeerings::ConfigurationAccordion::AwsCli
 *   @model={{@model}}
 *   @connectionMethod={{@connectionMethod}}
 *   @connection={{hash region=this.region cidrBlock=this.cidrBlock}}
 *   @products={{this.products}}
 *   @product={{@product}}
 * />
 * ```
 *
 * @class PageHvnPeeringsConfigurationAccordionAwsCli
 *
 */
export default class PageHvnPeeringsConfigurationAccordionAwsCli extends Component {
  @service router;

  @tracked awsSecurityGroupId;
  @tracked awsRouteTableId;

  scriptRegex = /\n/g;

  constructor(owner, args) {
    super(owner, args);
    this.awsSecurityGroupId = '';
    this.awsRouteTableId = '';
  }

  updateQueryParams = (queryParams = {}) => {
    return this.router.transitionTo(this.router.currentRouteName, {
      queryParams,
    });
  };

  get routeTableCommand() {
    let { cidrBlock, providerPeeringId, region } = this.args.connection;
    let { awsRouteTableId } = this;
    return `aws ec2 --region ${region}
    create-route --route-table-id ${awsRouteTableId}
    --destination-cidr-block ${cidrBlock}
    --vpc-peering-connection-id ${providerPeeringId}`.replace(
      this.scriptRegex,
      ' '
    );
  }

  get consulAuthorizeSecurityGroupIngress() {
    let { region, cidrBlock } = this.args.connection;
    let { awsSecurityGroupId } = this;
    let string = consulIngress({
      region: region,
      groupId: awsSecurityGroupId,
      cidrBlock: cidrBlock,
    });

    return string.replace(this.scriptRegex, ' ');
  }

  get consulAuthorizeSecurityGroupEgress() {
    let { region, cidrBlock } = this.args.connection;
    let { awsSecurityGroupId } = this;

    let string = consulEgressAgentless({
      region: region,
      groupId: awsSecurityGroupId,
      cidrBlock: cidrBlock,
    });

    return string.replace(this.scriptRegex, ' ');
  }

  get vaultAuthorizeSecurityGroupIngress() {
    let { region, cidrBlock } = this.args.connection;
    let { awsSecurityGroupId } = this;
    let string = vaultTemplate({
      region: region,
      groupId_flag: awsSecurityGroupId,
      cidrIp_tcp_port8200: cidrBlock,
      groupId_tcp_port8200: this.awsSecurityGroupId,
    });
    return string.replace(this.scriptRegex, ' ');
  }

  <template>
    <div
      class="page-hvn-peerings-configuration-accordion-aws-cli"
      {{boxPadding "xl 0 0 0"}}
    >
      <Accordion
        id="configure-aws-cli-accordion-one"
        @label={{t
          "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.aws-cli.accordion-one.title"
        }}
        @isInitiallyOpen={{unless @completion.securityGroupMarkedComplete true}}
        @displayBadge={{if
          (and @completion.securityGroupMarkedComplete @displayBadge)
          true
        }}
        @isSuccess={{true}}
      >
        <div>
          <div {{boxPadding "0 0 lg 0"}}>
            <Typography
              class="hds-typography-body-200 hds-foreground-primary"
              {{boxPadding "0 0 sm 0"}}
            >
              {{t
                "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.aws-cli.accordion-one.description"
                hvnID=@model.network.id
                htmlSafe=true
              }}
            </Typography>
            <HdsLinkStandalone
              @text={{t
                "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.aws-cli.accordion-one.learn-link"
              }}
              @icon="learn-link"
              @iconPosition="trailing"
              @href={{t
                "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.aws-cli.accordion-one.learn-link-url"
              }}
            />
          </div>
          <form
            aria-label={{t
              "components.page.hvn-peerings.detail.instructions.default.updating-groups.terminal.aria-label"
            }}
            class="hcp-hds-form"
            data-test-peering-terminal-updating-security-groups-form-aws
            {{boxPadding "0 0 xl 0"}}
          >
            <FlexGrid as |G|>
              <G.Item @xs="12" @sm="12" @md="12" @lg="4">
                <HdsFormTextInputField
                  @value={{this.awsSecurityGroupId}}
                  @isRequired={{true}}
                  {{on
                    "input"
                    (pick "target.value" (set this "awsSecurityGroupId"))
                  }}
                  as |F|
                >
                  <F.Label>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.terminal.label"
                    }}
                  </F.Label>
                  <F.HelperText>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.terminal.help-text"
                    }}
                  </F.HelperText>
                </HdsFormTextInputField>
                <DisclosureGuide
                  @content={{@awsDisclosureGuides.findSecurityGroupId}}
                />
              </G.Item>
            </FlexGrid>
          </form>
          <Typography
            class="hds-typography-body-200 hds-foreground-primary"
            {{boxPadding "0 0 sm 0"}}
          >
            {{t
              "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.aws-cli.accordion-one.script-prompt"
            }}
          </Typography>

          <HdsTabs
            data-test-peering-terminal-codeblock-tabs-aws
            {{boxPadding "0 0 xl 0"}}
            as |T|
          >
            <T.Tab
              @icon="vault"
              @isSelected={{eq @product @products.vault.id}}
              data-test-peering-terminal-vault-tab-aws
              {{on
                "click"
                (fn this.updateQueryParams (hash product=@products.vault.id))
              }}
            >
              {{t "components.networks.content-tabs.vault-snippets.label"}}
            </T.Tab>
            <T.Panel />

            <T.Tab
              @icon="consul-color"
              @isSelected={{eq @product @products.consul.id}}
              data-test-peering-terminal-consul-tab-aws
              {{on
                "click"
                (fn this.updateQueryParams (hash product=@products.consul.id))
              }}
            >
              {{t "components.networks.content-tabs.consul-snippets.label"}}
            </T.Tab>
            <T.Panel />
          </HdsTabs>

          {{#if (eq @product @products.consul.id)}}
            <div {{boxPadding "0 0 md 0"}}>
              <Typography
                @variant="h3"
                class="hds-typography-body-200 hds-font-weight-semibold hds-foreground-strong"
                {{boxPadding "0 0 md 0"}}
              >
                {{t
                  "components.page.hvn-peerings.detail.instructions.default.updating-groups.web-console.inbound-rules"
                }}
              </Typography>
              <HdsCodeBlock
                data-test-peering-terminal-consul-codeblock-inbound
                @value={{this.consulAuthorizeSecurityGroupIngress}}
                @hasCopyButton={{true}}
                @language="bash"
              />
            </div>
            <div>
              <Typography
                @variant="h3"
                class="hds-typography-body-200 hds-font-weight-semibold hds-foreground-strong"
                {{boxPadding "0 0 md 0"}}
              >
                {{t
                  "components.page.hvn-peerings.detail.instructions.default.updating-groups.web-console.outbound-rules"
                }}
              </Typography>
              <HdsCodeBlock
                data-test-peering-terminal-consul-codeblock-outbound
                class="autosize"
                @value={{this.consulAuthorizeSecurityGroupEgress}}
                @hasCopyButton={{true}}
                @language="bash"
              />
            </div>
          {{else if (eq @product @products.vault.id)}}
            <div>
              <Typography
                @variant="h3"
                class="hds-typography-body-200 hds-font-weight-semibold hds-foreground-strong"
                {{boxPadding "0 0 md 0"}}
              >
                {{t
                  "components.page.hvn-peerings.detail.instructions.default.updating-groups.web-console.outbound-rules"
                }}
              </Typography>
              <HdsCodeBlock
                data-test-peering-terminal-vault-codeblock-aws
                class="autosize"
                @value={{this.vaultAuthorizeSecurityGroupIngress}}
                @hasCopyButton={{true}}
                @language="bash"
              />
            </div>
          {{/if}}
          {{#if @displayBadge}}
            <div {{boxPadding "lg 0 0"}}>
              {{#unless @completion.securityGroupMarkedComplete}}
                <div {{boxPadding "0 0 md"}}>
                  <Typography>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.mark-complete-security-group-help"
                    }}
                  </Typography>
                </div>
              {{/unless}}
              <div>
                <HdsButton
                  @text={{t
                    (concat
                      "components.page.hvn-peerings.detail.instructions.aws.default."
                      (unless
                        @completion.securityGroupMarkedComplete
                        "mark-complete"
                        "mark-incomplete"
                      )
                    )
                  }}
                  @color="secondary"
                  @size="small"
                  {{on "click" @markSecurityGroupAsComplete}}
                />
              </div>
            </div>
          {{/if}}
        </div>
      </Accordion>
      <Accordion
        id="configure-aws-cli-accordion-two"
        @isInitiallyOpen={{this.accordionState.two}}
        @label={{t
          "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.aws-cli.accordion-two.title"
        }}
        @displayBadge={{if
          (and @completion.routeTableMarkedComplete @displayBadge)
          true
        }}
        @isSuccess={{true}}
      >
        <div>
          <div {{boxPadding "0 0 lg 0"}}>
            <Typography
              class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
              {{boxPadding "0 0 md 0"}}
            >
              {{t
                "components.page.hvn-peerings.detail.instructions.aws.default.updating-route-table.terminal.description"
                htmlSafe=true
              }}
            </Typography>
            <HdsLinkStandalone
              @text={{t
                "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.aws-cli.accordion-two.learn-link"
              }}
              @icon="learn-link"
              @iconPosition="trailing"
              @href={{t
                "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.aws-cli.accordion-two.learn-link-url"
              }}
            />
          </div>
          <form
            aria-label={{t
              "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.aws-cli.accordion-two.aria-label"
            }}
            class="hcp-hds-form"
            data-test-peering-terminal-updating-routing-table-form
          >
            <div {{boxPadding "0 0 lg 0"}}>
              <FlexGrid as |G|>
                <G.Item @xs="12" @sm="12" @md="12" @lg="4">
                  <HdsFormTextInputField
                    data-test-aws-route-table-id
                    @value={{this.awsRouteTableId}}
                    {{on
                      "input"
                      (pick "target.value" (set this "awsRouteTableId"))
                    }}
                    as |F|
                  >
                    <F.Label>
                      {{t
                        "components.page.hvn-peerings.detail.instructions.aws.default.updating-route-table.terminal.label"
                      }}
                    </F.Label>
                    <F.HelperText>
                      {{t
                        "components.page.hvn-peerings.detail.instructions.aws.default.updating-route-table.terminal.help-text"
                      }}
                    </F.HelperText>
                  </HdsFormTextInputField>
                  <DisclosureGuide
                    @content={{@awsDisclosureGuides.findRouteTableId}}
                  />
                </G.Item>
              </FlexGrid>
            </div>
            <div>
              <HdsCodeBlock
                class="autosize"
                @value={{this.routeTableCommand}}
                @hasCopyButton={{true}}
                @language="bash"
              />
            </div>
          </form>
          {{#if @displayBadge}}
            <div {{boxPadding "lg 0 0"}}>
              {{#unless @completion.routeTableMarkedComplete}}
                <div {{boxPadding "0 0 md"}}>
                  <Typography>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.mark-complete-route-table-help"
                    }}
                  </Typography>
                </div>
              {{/unless}}
              <div>
                <HdsButton
                  @text={{t
                    (concat
                      "components.page.hvn-peerings.detail.instructions.aws.default."
                      (unless
                        @completion.routeTableMarkedComplete
                        "mark-complete"
                        "mark-incomplete"
                      )
                    )
                  }}
                  @color="secondary"
                  @size="small"
                  {{on "click" @markRouteTableAsComplete}}
                />
              </div>
            </div>
          {{/if}}
        </div>
      </Accordion>
      <Accordion
        id="configure-aws-cli-accordion-three"
        @isInitiallyOpen={{this.accordionState.three}}
        @label={{t
          "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.aws-cli.accordion-three.title"
        }}
        @displayBadge={{true}}
        @isOptional={{true}}
      >
        <div>
          <Typography
            class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
            {{boxPadding "0 0 sm 0"}}
          >
            {{t
              "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.aws-cli.accordion-three.help-text"
              hvnID=@model.network.id
              htmlSafe=true
            }}
          </Typography>
          <HdsLinkStandalone
            @text={{t
              "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.aws-cli.accordion-three.learn-link"
            }}
            @icon="learn-link"
            @iconPosition="trailing"
            @href={{t
              "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.aws-cli.accordion-three.learn-link-url"
            }}
          />
        </div>
        <List @isHvnHvnPeering={{this.isHvnHvnPeering}} @model={{@model}} />
      </Accordion>
    </div>
  </template>
}
